import React, { useState, useRef, useEffect } from 'react';
import {
  Card,
  Button,
  Select,
  InputNumber,
  Table,
  Tag,
  Space,
  Spin,
  Typography,
  message,
  Result,
  Empty,
  Row,
  Col,
  Statistic,
  Progress,
  Descriptions,
  Slider,
} from 'antd';
import {
  RobotOutlined,
  SyncOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { proposalApi } from '../../services/proposal';
import { POLL_CONFIG, showPollTimeoutNotification } from '../../utils/polling';
import type { PricingPlan, RequirementAnalysis, PricingMatrixItem } from '../../types/proposal';
import { COLORS } from '../../constants/colors';
import { CONFIDENCE_MAP } from '../../constants/statusMappings';

const { Text } = Typography;

// 粉丝分层选项
const followerTierOptions = [
  { value: 'nano', label: 'Nano (1K-10K)' },
  { value: 'micro', label: 'Micro (10K-100K)' },
  { value: 'mid', label: 'Mid (100K-500K)' },
  { value: 'macro', label: 'Macro (500K-1M)' },
  { value: 'mega', label: 'Mega (1M+)' },
];

// 内容类型选项
const contentTypeOptions = [
  { value: 'video', label: '视频' },
  { value: 'short_video', label: '短视频' },
  { value: 'live', label: '直播' },
  { value: 'post', label: '图文' },
  { value: 'story', label: 'Story' },
  { value: 'reel', label: 'Reels' },
];

// 国家选项
const countryOptions = [
  { value: 'US', label: '美国' },
  { value: 'UK', label: '英国' },
  { value: 'DE', label: '德国' },
  { value: 'FR', label: '法国' },
  { value: 'JP', label: '日本' },
  { value: 'KR', label: '韩国' },
  { value: 'AU', label: '澳大利亚' },
  { value: 'CA', label: '加拿大' },
  { value: 'BR', label: '巴西' },
  { value: 'MX', label: '墨西哥' },
];

// 平台选项
const platformOptions = [
  { value: 'youtube', label: 'YouTube' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'twitter', label: 'Twitter/X' },
  { value: 'facebook', label: 'Facebook' },
];

// 数据来源标签
const getDataSourceTag = (source: string) => {
  if (source === 'knowledge_base') {
    return <Tag color="blue">知识库驱动</Tag>;
  }
  if (source === 'cpm_estimate') {
    return <Tag color="orange">CPM 估算</Tag>;
  }
  return <Tag>{source}</Tag>;
};

// 置信度颜色和文本
const getConfidenceConfig = (level: string) =>
  CONFIDENCE_MAP[level] || { color: COLORS.borderLight, text: level, tagColor: 'default' };

// 格式化货币
const formatCurrency = (amount: number, currency: string = 'USD'): string => {
  const symbol = currency === 'CNY' ? '¥' : '$';
  return `${symbol}${amount.toLocaleString()}`;
};

interface PricingTabProps {
  proposalId: string;
  pricing?: PricingPlan | null;
  requirement?: RequirementAnalysis | null;
  onRefresh: () => void;
}

export const PricingTab: React.FC<PricingTabProps> = ({
  proposalId,
  pricing,
  requirement,
  onRefresh,
}) => {
  // 参数状态
  const [targetCountries, setTargetCountries] = useState<string[]>([]);
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [followerTiers, setFollowerTiers] = useState<string[]>([]);
  const [contentTypes, setContentTypes] = useState<string[]>([]);
  const [serviceFeeRate, setServiceFeeRate] = useState<number>(10);
  const [loading, setLoading] = useState(false);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 从需求中自动填充参数
  useEffect(() => {
    if (requirement?.parsed_result) {
      const result = requirement.parsed_result as Record<string, unknown>;
      if (Array.isArray(result.target_countries)) {
        setTargetCountries(result.target_countries.map(String));
      }
      if (result.target_platform) {
        setPlatforms(Array.isArray(result.target_platform) 
          ? result.target_platform.map(String) 
          : [String(result.target_platform)]);
      }
      if (Array.isArray(result.follower_tiers)) {
        setFollowerTiers(
          result.follower_tiers.map((item: unknown) => {
            if (typeof item === 'object' && item !== null) {
              const obj = item as Record<string, unknown>;
              return String(obj.tier || obj.name || obj.range || JSON.stringify(item));
            }
            return String(item);
          })
        );
      }
      if (Array.isArray(result.content_types)) {
        setContentTypes(result.content_types.map(String));
      }
    }
  }, [requirement]);

  // 从已有报价中恢复服务费率
  useEffect(() => {
    if (pricing?.service_fee_rate !== undefined) {
      setServiceFeeRate(pricing.service_fee_rate * 100);
    }
  }, [pricing]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // 生成报价
  const handleGenerate = async () => {
    if (!proposalId) {
      message.error('提案ID不存在');
      return;
    }
    setLoading(true);
    try {
      await proposalApi.generatePricing(proposalId, {
        service_fee_rate: serviceFeeRate / 100,
      });
      message.success('AI报价生成已启动');
      startPolling();
    } catch (error) {
      console.error('[PricingTab] 报价生成失败:', error);
      message.error('启动报价生成失败，请重试');
      setLoading(false);
    }
  };

  // 轮询检查状态
  const startPolling = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    let pollAttempts = 0;
    timerRef.current = setInterval(async () => {
      pollAttempts++;
      if (pollAttempts >= POLL_CONFIG.MAX_ATTEMPTS) {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        showPollTimeoutNotification('poll-timeout-pricing', () => {
          startPolling();
        });
        setLoading(false);
        return;
      }
      try {
        const response = await proposalApi.getPricing(proposalId);
        const result = response.data;
        if (result && (result.status === 'completed' || result.status === 'failed')) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }
          setLoading(false);
          onRefresh();
          if (result.status === 'failed') {
            message.error('报价生成失败');
          } else {
            message.success('报价生成完成');
          }
        }
      } catch (error) {
        // 轮询中忽略单个请求错误，继续轮询
        console.warn('[PricingTab] 轮询报价状态失败:', error);
      }
    }, POLL_CONFIG.INTERVAL);
  };

  // 更新服务费率
  const handleServiceFeeChange = async (value: number) => {
    setServiceFeeRate(value);
    if (pricing?.id) {
      try {
        await proposalApi.updatePricing(pricing.id, {
          service_fee_rate: value / 100,
        });
        message.success('服务费率已更新');
      } catch (error) {
        console.error('[PricingTab] 更新服务费率失败:', error);
        message.error('更新服务费率失败');
      }
    }
  };

  // 报价矩阵表格列配置
  const columns = [
    {
      title: '平台',
      dataIndex: 'platform',
      key: 'platform',
      render: (text: string) => <Tag>{text}</Tag>,
      width: 100,
    },
    {
      title: '内容类型',
      dataIndex: 'content_type',
      key: 'content_type',
      width: 100,
    },
    {
      title: '粉丝量级',
      dataIndex: 'follower_tier',
      key: 'follower_tier',
      width: 120,
    },
    {
      title: '预估 CPM',
      dataIndex: 'estimated_cpm',
      key: 'estimated_cpm',
      width: 120,
      render: (value: number, record: PricingMatrixItem) =>
        formatCurrency(value, record.currency),
    },
    {
      title: '价格区间',
      dataIndex: 'estimated_price_range',
      key: 'estimated_price_range',
      width: 180,
      render: (range: { min: number; max: number }, record: PricingMatrixItem) => (
        <Text strong>
          {formatCurrency(range.min, record.currency)} - {formatCurrency(range.max, record.currency)}
        </Text>
      ),
    },
    {
      title: '数据来源',
      dataIndex: 'data_source',
      key: 'data_source',
      width: 120,
      render: (source: string) => getDataSourceTag(source),
    },
  ];

  // 前置检查
  if (!requirement || (requirement.status !== 'draft' && requirement.status !== 'confirmed')) {
    return (
      <Card>
        <Empty description="请先完成需求解构后生成报价" />
      </Card>
    );
  }

  // 处理中状态
  if (pricing?.status === 'processing' || loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 24 }}>
            <Text type="secondary" style={{ fontSize: 16 }}>
              AI 正在生成报价方案，请稍候...
            </Text>
          </div>
        </div>
      </Card>
    );
  }

  // 失败状态
  if (pricing?.status === 'failed') {
    return (
      <Card>
        <Result
          status="error"
          title="报价生成失败"
          subTitle="报价生成过程中发生错误，请重试"
          extra={
            <Button
              type="primary"
              icon={<SyncOutlined />}
              onClick={handleGenerate}
            >
              重新生成
            </Button>
          }
        />
      </Card>
    );
  }

  // 有报价结果
  const hasResult = pricing?.status === 'completed';

  return (
    <Card>
      {/* 参数面板 */}
      <Card title="报价参数" size="small" style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>目标国家</Text>
            </div>
            <Select
              mode="multiple"
              placeholder="请选择目标国家"
              value={targetCountries}
              onChange={setTargetCountries}
              options={countryOptions}
              style={{ width: '100%' }}
              allowClear
            />
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>平台</Text>
            </div>
            <Select
              mode="multiple"
              placeholder="请选择平台"
              value={platforms}
              onChange={setPlatforms}
              options={platformOptions}
              style={{ width: '100%' }}
              allowClear
            />
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>粉丝分层</Text>
            </div>
            <Select
              mode="multiple"
              placeholder="请选择粉丝分层"
              value={followerTiers}
              onChange={setFollowerTiers}
              style={{ width: '100%' }}
              allowClear
            >
              {followerTierOptions.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  <Tag>{opt.label}</Tag>
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>内容类型</Text>
            </div>
            <Select
              mode="multiple"
              placeholder="请选择内容类型"
              value={contentTypes}
              onChange={setContentTypes}
              style={{ width: '100%' }}
              allowClear
            >
              {contentTypeOptions.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  <Tag>{opt.label}</Tag>
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>服务费率 (%)</Text>
            </div>
            <Row gutter={16} align="middle">
              <Col span={18}>
                <Slider
                  min={0}
                  max={50}
                  value={serviceFeeRate}
                  onChange={(v) => setServiceFeeRate(v)}
                  onChangeComplete={handleServiceFeeChange}
                  tooltip={{ formatter: (v) => `${v}%` }}
                />
              </Col>
              <Col span={6}>
                <InputNumber
                  min={0}
                  max={50}
                  value={serviceFeeRate}
                  onChange={(v) => v !== null && handleServiceFeeChange(v)}
                  formatter={(value) => `${value}%`}
                  parser={(value) => Number(value?.replace('%', '') || 0)}
                  style={{ width: '100%' }}
                />
              </Col>
            </Row>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: 8, visibility: 'hidden' }}>
              <Text>占位</Text>
            </div>
            <Button
              type="primary"
              icon={<RobotOutlined />}
              onClick={handleGenerate}
              loading={loading}
              size="large"
            >
              {hasResult ? '重新生成报价' : '生成报价'}
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 报价结果 */}
      {hasResult && pricing && (
        <>
          {/* 置信度 */}
          {pricing.confidence_level && (
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Text strong>置信度：</Text>
                <Tag color={getConfidenceConfig(pricing.confidence_level).tagColor}>
                  {getConfidenceConfig(pricing.confidence_level).text}
                </Tag>
              </Space>
            </div>
          )}

          {/* 总预算估算 */}
          {pricing.total_budget_estimate && (
            <Card
              title={
                <Space>
                  <DollarOutlined />
                  <span>总预算估算</span>
                </Space>
              }
              size="small"
              style={{ marginBottom: 24 }}
            >
              <Row gutter={24}>
                <Col span={8}>
                  <Statistic
                    title="最低预算"
                    value={pricing.total_budget_estimate.min}
                    prefix={pricing.total_budget_estimate.currency === 'CNY' ? '¥' : '$'}
                    valueStyle={{ color: COLORS.dangerDeep }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="推荐预算"
                    value={pricing.total_budget_estimate.recommended}
                    prefix={pricing.total_budget_estimate.currency === 'CNY' ? '¥' : '$'}
                    valueStyle={{ color: 'var(--color-primary)' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="最高预算"
                    value={pricing.total_budget_estimate.max}
                    prefix={pricing.total_budget_estimate.currency === 'CNY' ? '¥' : '$'}
                    valueStyle={{ color: COLORS.successDeep }}
                  />
                </Col>
              </Row>
            </Card>
          )}

          {/* 数据来源统计 */}
          {pricing.data_source_summary && (
            <Card title="数据来源统计" size="small" style={{ marginBottom: 24 }}>
              <Row gutter={24}>
                <Col span={8}>
                  <Space direction="vertical" align="center" style={{ width: '100%' }}>
                    <Progress
                      type="circle"
                      percent={Math.round(pricing.data_source_summary.knowledge_base_ratio * 100)}
                      width={80}
                      strokeColor={COLORS.primary}
                    />
                    <Text>知识库命中率</Text>
                  </Space>
                </Col>
                <Col span={16}>
                  <Descriptions column={2} size="small">
                    <Descriptions.Item label="知识库数据">
                      <Tag color="blue">{pricing.data_source_summary.knowledge_base_count} 条</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="CPM估算数据">
                      <Tag color="orange">{pricing.data_source_summary.cpm_estimate_count} 条</Tag>
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
              </Row>
            </Card>
          )}

          {/* 报价矩阵 */}
          <Card title="报价矩阵" size="small" style={{ marginBottom: 24 }}>
            <Table
              dataSource={pricing.pricing_matrix || []}
              columns={columns}
              rowKey={(record) =>
                `${record.platform}-${record.content_type}-${record.follower_tier}`
              }
              pagination={false}
              size="small"
              scroll={{ x: 800 }}
            />
          </Card>

          {/* 知识库参考项目 */}
          {pricing.kb_reference_projects && pricing.kb_reference_projects.length > 0 && (
            <Card title="知识库参考项目" size="small">
              <Table
                dataSource={pricing.kb_reference_projects}
                columns={[
                  {
                    title: '项目名称',
                    dataIndex: 'project_name',
                    key: 'project_name',
                  },
                  {
                    title: '行业',
                    dataIndex: 'industry',
                    key: 'industry',
                    render: (text: string) => <Tag>{text}</Tag>,
                  },
                  {
                    title: '平台',
                    dataIndex: 'platform',
                    key: 'platform',
                    render: (text: string) => <Tag color="blue">{text}</Tag>,
                  },
                  {
                    title: '价格',
                    dataIndex: 'price',
                    key: 'price',
                    render: (value: number, record) =>
                      formatCurrency(value, record.currency),
                  },
                ]}
                rowKey="project_id"
                pagination={false}
                size="small"
              />
            </Card>
          )}
        </>
      )}

      {/* 暂无数据 */}
      {!hasResult && !loading && (
        <Empty
          description="暂无报价数据，请设置参数后生成报价"
          style={{ marginTop: 24 }}
        />
      )}
    </Card>
  );
};
